import { TextSelection } from "@tiptap/pm/state";
import { Extension } from "@tiptap/react";

/**
 * Tab navigation between document sections.
 *
 * Tab moves focus to the next top-level section in the document.
 * Shift-Tab moves focus to the previous section.
 *
 * For atom nodes (e.g., AtomicField), focuses the first interactive
 * element inside the node's DOM (button, input, or [tabindex] element).
 * For content nodes, places a text cursor at the start (Tab) or end (Shift-Tab).
 */
export const TabNavigation = Extension.create({
  name: "tabNavigation",

  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        const { $from } = editor.state.selection;
        if ($from.depth < 1) return false;

        // If cursor is inside a list item or code block within a richSection,
        // let the default keymaps handle Tab (list indent, code tab insertion)
        const sectionNode = $from.node(1);
        if (sectionNode?.type.name === "richSection") {
          for (let d = $from.depth; d > 1; d--) {
            const name = $from.node(d).type.name;
            if (name === "listItem" || name === "codeBlock") return false;
          }
        }

        // Position just after the current section
        const afterPos = $from.after(1);
        if (afterPos >= editor.state.doc.content.size) return false;

        const nextNode = editor.state.doc.resolve(afterPos).nodeAfter;
        if (!nextNode) return false;

        if (nextNode.isAtom) {
          // Atom nodes can't hold a text cursor — focus DOM interactive element
          const domNode = editor.view.nodeDOM(afterPos);
          if (domNode instanceof HTMLElement) {
            const focusable = domNode.querySelector<HTMLElement>("button, input, [tabindex]");
            if (focusable) {
              focusable.focus();
              return true;
            }
          }
          return true;
        }

        // Content node — place cursor at start of next section
        const selection = TextSelection.near(editor.state.doc.resolve(afterPos + 1));
        editor.view.dispatch(editor.state.tr.setSelection(selection));
        return true;
      },

      "Shift-Tab": ({ editor }) => {
        const { $from } = editor.state.selection;
        if ($from.depth < 1) return false;

        // If cursor is inside a list item or code block within a richSection,
        // let the default keymaps handle Shift-Tab (list outdent)
        const sectionNode = $from.node(1);
        if (sectionNode?.type.name === "richSection") {
          for (let d = $from.depth; d > 1; d--) {
            const name = $from.node(d).type.name;
            if (name === "listItem" || name === "codeBlock") return false;
          }
        }

        // Position just before the current section
        const beforePos = $from.before(1);
        if (beforePos <= 0) return false;

        const prevNode = editor.state.doc.resolve(beforePos).nodeBefore;
        if (!prevNode) return false;

        if (prevNode.isAtom) {
          const domNode = editor.view.nodeDOM(beforePos - prevNode.nodeSize);
          if (domNode instanceof HTMLElement) {
            const focusable = domNode.querySelector<HTMLElement>("button, input, [tabindex]");
            if (focusable) {
              focusable.focus();
              return true;
            }
          }
          return true;
        }

        // Content node — place cursor at end of previous section
        const selection = TextSelection.near(editor.state.doc.resolve(beforePos - 1), -1);
        editor.view.dispatch(editor.state.tr.setSelection(selection));
        return true;
      },
    };
  },
});
